import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Input, Spin, Typography, Space, Empty, Tag } from "antd";
import {
  SearchOutlined,
  FileOutlined,
  TeamOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { ragSearch } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { markdownComponents } from "./MarkdownLink";
import { ditColors } from "../lib/theme";
import { APP_BRAND_NAME } from "../lib/brand";

interface SearchResult {
  collection: string;
  id: string;
  title: string;
  snippet: string;
  score: number;
  link: string;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const ICONS: Record<string, React.ReactNode> = {
  projects: <FileOutlined aria-hidden="true" />,
  vendorsPublic: <TeamOutlined aria-hidden="true" />,
  publicIntakes: <InboxOutlined aria-hidden="true" />,
};

const COLLECTION_LABELS: Record<string, string> = {
  projects: "Project",
  vendorsPublic: "Vendor",
  publicIntakes: "Intake",
};

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [answer, setAnswer] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const resultRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!debouncedQuery.trim() || !open) {
      setResults([]);
      setAnswer("");
      setActiveIndex(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await ragSearch(debouncedQuery, ["projects", "vendorsPublic", "publicIntakes"], 5);
        if (cancelled) return;
        setResults(Array.isArray(data.results) ? data.results : []);
        setAnswer(data.answer || "");
        setActiveIndex(0);
      } catch (err) {
        if (!cancelled) {
          setResults([]);
          setAnswer("");
        }
        // eslint-disable-next-line no-console
        console.error("CommandPalette search failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open]);

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      navigate(result.link);
      onClose();
    },
    [navigate, onClose]
  );

  const handleModalClose = useCallback(() => {
    setQuery("");
    setResults([]);
    setAnswer("");
    setActiveIndex(0);
    onClose();
  }, [onClose]);

  // Keep a ref to the latest state so the document-level keydown listener
  // (registered once per `open` toggle) always reads fresh values.
  const stateRef = useRef({ results, activeIndex });
  stateRef.current = { results, activeIndex };

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      const { results: r, activeIndex: ai } = stateRef.current;

      if (e.key === "Escape") {
        e.stopPropagation();
        handleModalClose();
        return;
      }

      if (e.key === "ArrowDown") {
        if (r.length === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % r.length);
        return;
      }

      if (e.key === "ArrowUp") {
        if (r.length === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + r.length) % r.length);
        return;
      }

      if (e.key === "Enter") {
        const result = r[ai];
        if (result) {
          e.preventDefault();
          handleResultClick(result);
        }
        return;
      }

      if (e.key === "Tab" && containerRef.current) {
        const focusables = containerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [role="option"], [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, handleModalClose, handleResultClick]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    const el = resultRefs.current[activeIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, results.length]);

  const announcement = loading
    ? "Searching"
    : results.length === 0 && debouncedQuery
      ? "No results found"
      : results.length > 0
        ? `${results.length} ${results.length === 1 ? "result" : "results"} found`
        : "";

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      footer={null}
      width={720}
      destroyOnClose
      title={
        <Space>
          <SearchOutlined aria-hidden="true" />
          <span id="command-palette-title">Search {APP_BRAND_NAME}</span>
        </Space>
      }
      styles={{ body: { maxHeight: "60vh", overflowY: "auto" } }}
      modalRender={(node) => (
        <div ref={containerRef} aria-busy={loading}>
          {node}
        </div>
      )}
    >
      <Input
        size="large"
        placeholder="Search projects, vendors, and intakes..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        prefix={<SearchOutlined aria-hidden="true" />}
        autoFocus
        aria-label="Search query"
        aria-controls="command-palette-results"
        aria-activedescendant={results.length > 0 ? `cp-option-${activeIndex}` : undefined}
        style={{ marginBottom: 16 }}
      />

      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {announcement}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0" }} role="status">
          <Spin tip="Searching..." />
        </div>
      )}

      {!loading && answer && (
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            background: ditColors.secondaryLight,
            color: ditColors.primary,
            borderRadius: 8,
          }}
          className="markdown-content"
        >
          <Typography.Title level={5} style={{ marginTop: 0, color: ditColors.primary }}>
            AI Answer
          </Typography.Title>
          <ReactMarkdown components={markdownComponents}>{answer}</ReactMarkdown>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div>
          <Typography.Text type="secondary" style={{ marginBottom: 8, display: "block" }}>
            Results ({results.length})
          </Typography.Text>
          <ul
            id="command-palette-results"
            role="listbox"
            aria-label="Search results"
            style={{ listStyle: "none", padding: 0, margin: 0 }}
          >
            {results.map((result, idx) => {
              const isActive = idx === activeIndex;
              return (
                <li
                  id={`cp-option-${idx}`}
                  key={`${result.collection}-${result.id}`}
                  role="option"
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  ref={(el) => {
                    resultRefs.current[idx] = el;
                  }}
                  onClick={() => handleResultClick(result)}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onFocus={() => setActiveIndex(idx)}
                  style={{
                    cursor: "pointer",
                    padding: "12px 12px",
                    borderRadius: 6,
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    background: isActive ? ditColors.secondaryLight : "transparent",
                    color: isActive ? ditColors.primary : "inherit",
                    outline: "none",
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      fontSize: 18,
                      color: isActive ? ditColors.primary : ditColors.secondaryDark,
                      paddingTop: 2,
                    }}
                  >
                    {ICONS[result.collection] ?? <FileOutlined />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <strong style={{ color: isActive ? ditColors.primary : undefined }}>
                        {result.title}
                      </strong>
                      <Tag
                        style={{
                          background: ditColors.secondaryLight,
                          color: ditColors.primary,
                          borderColor: ditColors.secondaryDark,
                        }}
                      >
                        {COLLECTION_LABELS[result.collection] ?? result.collection}
                      </Tag>
                    </div>
                    {result.snippet && (
                      <div
                        style={{
                          fontSize: 13,
                          color: isActive ? ditColors.primary : "rgba(0,0,0,0.65)",
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {result.snippet}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!loading && query && debouncedQuery && results.length === 0 && (
        <Empty
          description="Try a different query."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: "40px 0" }}
        />
      )}

      {!loading && !query && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Typography.Text type="secondary">
            Start typing to search across projects, vendors, and intakes.
          </Typography.Text>
        </div>
      )}
    </Modal>
  );
};
